import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { toast } from "sonner";

import { captureException } from "../../sentry.config";
import { describeError, isExpectedError, toAppError } from "../lib/errors";

/**
 * Client-side error reporting.
 *
 * This is the single funnel for "something failed while the user was clicking
 * around". It does three things at once, which is why call sites should use it
 * instead of `toast.error(e.message)`:
 *
 *   1. Normalizes whatever was thrown into an `AppError` so the user sees the
 *      catalog's friendly copy rather than a stack trace or a raw Postgres
 *      message.
 *   2. Forwards the error to Sentry with the catalog code as an indexed tag, so
 *      incidents can be filtered by code ("every DUPLICATE this week").
 *   3. Stores it in context so the superadmin-only details panel can render the
 *      technical cause without the message being shown to everyone.
 *
 * SECURITY
 * --------
 * `reportError` never puts the raw cause in the toast, and it never decides
 * what the superadmin may see -- that decision was already made on the server in
 * `toClientError` before the error ever reached this browser. What arrives here
 * for an ordinary user is a generic message with an empty `detail`.
 */

const ErrorReporterContext = createContext(null);

/**
 * Builds the provider. Mounted once, in the root route, so that a failure deep
 * in a screen still has somewhere to report itself.
 */
export function ErrorReporterProvider({ children }) {
  /**
   * @type {[null | { error: import("../lib/errors").AppError, eventId?: string, at: number, retry?: Function }, Function]}
   */
  const [lastError, setLastError] = useState(null);

  /**
   * Report a failed operation: toast the friendly copy, send to Sentry, and
   * retain the error for the superadmin panel.
   *
   * @param {unknown} error The thrown value. Plain errors from server
   *   functions arrive here already normalized and rehydrated by Seroval, so
   *   their catalog code is preserved.
   * @param {object} [options]
   * @param {string} [options.area] Operation identifier, e.g. "quiz.save". Used
   *   as a Sentry tag, so keep it low-cardinality and consistent.
   * @param {Function} [options.retry] Re-run handler for the "Try again"
   *   button in the details panel.
   * @returns {import("../lib/errors").AppError} The normalized error, for
   *   callers that want to branch on the code.
   */
  const reportError = useCallback((error, { area = "unknown", retry } = {}) => {
    const appError = toAppError(error, { area });

    // Deliberately `isSuperadmin: false` here. This view model is what every
    // user sees; the panel re-derives its own with the detail included.
    const view = describeError(appError, { isSuperadmin: false });

    // A wrong PIN or a missing title is the system working correctly, so it is
    // recorded at `info` and tagged, keeping it queryable without inflating the
    // error-rate trend that says whether something is actually broken.
    const expected = isExpectedError(appError.code);

    const eventId = captureException(appError, {
      level: expected ? "info" : "error",
      tags: { code: appError.code, area, expected: String(expected) },
      extra: {
        area: appError.area || area,
        hint: view.hint,
        retryable: view.retryable,
      },
    });

    // The toast carries only friendly copy: what happened, and what the user
    // can do next. The Sentry event id is deliberately NOT shown - a
    // 32-character hex string reads as a crash to the person seeing it. It
    // stays on the stored error for the superadmin details panel, and Sentry
    // attributes the event to the signed-in user without it.
    toast.error(view.message, { description: view.hint || undefined });

    setLastError({ error: appError, eventId, at: Date.now(), retry });

    return appError;
  }, []);

  /** Dismiss the stored error (the panel calls this once expanded or closed). */
  const clearError = useCallback(() => setLastError(null), []);

  /**
   * Record an error WITHOUT reporting it.
   *
   * Used by the route error boundary. The router's `defaultOnCatch` has already
   * sent that error to Sentry by the time the boundary renders, so calling
   * `reportError` there would produce a duplicate event and a second toast on
   * top of the error screen the user is already looking at. This only populates
   * the panel.
   *
   * @param {unknown} error The thrown value.
   * @param {object} [options]
   * @param {string} [options.area] Operation identifier.
   * @param {string} [options.eventId] Sentry event id, when one is known. The
   *   error boundary reads it from `lastSentryEventId()`; it is shown only in
   *   the superadmin panel.
   * @returns {import("../lib/errors").AppError} The normalized error.
   */
  const recordError = useCallback((error, { area = "route", eventId } = {}) => {
    const appError = toAppError(error, { area });
    setLastError({ error: appError, eventId, at: Date.now() });
    return appError;
  }, []);

  // Memoized so that a re-render of the provider does not re-render every
  // consumer of the hook, which would include whole route trees.
  const value = useMemo(
    () => ({ lastError, reportError, recordError, clearError }),
    [lastError, reportError, recordError, clearError],
  );

  return <ErrorReporterContext.Provider value={value}>{children}</ErrorReporterContext.Provider>;
}

/**
 * Access the error reporter from any component.
 *
 * Throws when no provider is mounted above the caller, because silently doing
 * nothing would drop error reports without any signal.
 *
 * @returns {{ lastError: null | { error: object, eventId?: string, at: number, retry?: Function }, reportError: Function, recordError: Function, clearError: Function }}
 */
export function useErrorReporter() {
  const context = useContext(ErrorReporterContext);
  if (context === null) {
    throw new Error("useErrorReporter must be used inside <ErrorReporterProvider>");
  }
  return context;
}
